import React, { useEffect, useMemo, useRef, useState } from "react";

import chunk from "lodash-es/chunk";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { MultiSelect, MultiSelectChangeEvent } from "primereact/multiselect";
import { useResizeDetector } from "react-resize-detector";
import { Tooltip } from "react-tooltip";
import { T } from "services/lang/LanguageService";
import useDashboardStore from "store/dashboard-store";
import useDatasetIdentifiersStore from "store/dataset-identifiers-store";
import useScadaStore from "store/scada-store";
import useSelectedScadaTagsStore from "store/selected-scada-tags-store";
import CalendarWrapper from "../../basic/calendar-wrapper/CalendarWrapper";
import "./Scada.scss";
import ScadaFreeTexts from "./components/ScadaFreeTexts";
import ScadaPolygons from "./components/ScadaPolygons";
import ScadaTags from "./components/ScadaTags";
import { ScadaProps, ScadaTagsProps } from "./scadaTypes";
import { defaultDateTime, defaultElementParameters } from "./utils/default-values";
import { getScadaImageUrl, showCords, tempId } from "./utils/scadaUtils";

const CHUNK_SIZE = 16;

export default function Scada(props: ScadaProps) {
  const [parent, setParent] = useState(defaultElementParameters);
  const [dateTime, setDateTime] = useState(defaultDateTime);
  const [layers, setLayers] = useState<string[]>([]);
  const [gridSize, setGridSize] = useState(0.5);
  const isEditMode = useDashboardStore(state => state.isEditMode);
  const ref = useRef<HTMLDivElement>(null);
  const {width, height} = useResizeDetector<HTMLDivElement>({targetRef : ref});

  const scadaStore = useScadaStore();
  const selectedIdsStore = useDatasetIdentifiersStore();
  const selectedTagsStore = useSelectedScadaTagsStore();

  const selectedTags = selectedTagsStore.tags;

  const scada = scadaStore.scadaMap.get(props.id ?? tempId(props));
  const layerOptions = useMemo(() => scada?.labelLayers ?? [], [scada?.labelLayers]);

  useEffect(() => {
    if (!scada) {
      scadaStore.addScada({
        scadaId: props.id ?? tempId(props),
        labels: props.elements ?? [],
        labelLayers: props.labelLayers ?? [],
        polygons: props.polygons ?? [],
        freeTexts: props.freeTexts ?? [],
      });
    } else {
      scadaStore.updateScadaLabels(props.id!, props.elements ?? []);
      scadaStore.updatePolygons(props.id!, props.polygons ?? []);
      scadaStore.updateFreeTexts(props.id!, props.freeTexts ?? []);
    }
  }, []);

  useEffect(() => {
      const rect = ref.current!.getBoundingClientRect();
      setParent({
        width: width ?? rect.width ?? 0,
        height: height ?? rect.height ?? 0,
        top: rect.top,
        left: rect.left,
      });
  }, [height, ref, width]);

  useEffect(() => {
    setLayers(layerOptions.filter(g => g.visible).map(g => g.layer));
  }, [layerOptions]);


  useEffect(() => {
    const updateDate = () => {
      setDateTime(prev => (!prev.manualTime ? { ...prev, date: new Date() } : prev));
    };

    const millisecondsUntilNextMinute = 60000 - (new Date().getTime() % 60000);

    const currentTimeOut = setTimeout(() => {
      updateDate();
      const interval = setInterval(updateDate, 60000);
      return () => clearInterval(interval);
    }, millisecondsUntilNextMinute);

    return () => clearTimeout(currentTimeOut);
  }, []);

  const handleLayerMultiselect = (e: MultiSelectChangeEvent) => {
    const updLayers = layerOptions.map(l => ({
      ...l,
      visible: e.value.includes(l.layer),
    }));
    scadaStore.updateScadaLabelLayers(props.id!, updLayers);
    setLayers(e.value);
  };

  const elemProps: ScadaTagsProps = {
    ...props,
    date: dateTime.manualTime ? dateTime.date : undefined,
    returnDate: e => setDateTime(prev => ({ ...prev, date: e })),
    parent,
    layerOptions,
    gridSize,
  };

  const cancelSelections = (e: React.MouseEvent) => {
    if (e.ctrlKey) return;

    if (new Date().getTime() - scadaStore.lastClick < 500) return;

    selectedTags.forEach(el => el.children[0].classList.remove("selectedLabel"));
    selectedTagsStore.clear();
    selectedIdsStore.clear();
  };

  const chunkedElements = chunk(props.elements, CHUNK_SIZE);
  return (
    <div className="flex flex-column gap-3">
      <div className="flex justify-content-between">
        {!props.hideHeader && props.showScadaSettings && (
          <div className="flex gap-1">
            <CalendarWrapper
              value={dateTime.date}
              onChange={e => setDateTime({ date: e, manualTime: true })}
            />
            {dateTime.manualTime && (
              <Button
                icon="pi pi-sync"
                disabled={!dateTime.manualTime}
                className="p-button-sm"
                onClick={() => setDateTime({ date: new Date(), manualTime: false })}
              />
            )}
          </div>
        )}

        <div className="flex gap-5 justify-content-end w-full">
          {isEditMode && !props.hideHeader && (
            <div className="flex gap-5 align-items-baseline">
              <div className="flex gap-2 align-items-center">
                <label htmlFor="gridSize">{T("word.grid")}, %:</label>
                <InputNumber
                  inputId="gridSize"
                  value={gridSize}
                  onChange={e => setGridSize(e.value ?? 1)}
                  inputStyle={{ width: "3rem" }}
                  maxFractionDigits={1}
                />
              </div>
            </div>
          )}

          {layerOptions.length > 1 && (
            <MultiSelect
              value={layers}
              onChange={handleLayerMultiselect}
              options={layerOptions.map(g => g.layer)}
              className="w-8rem"
              maxSelectedLabels={0}
              selectedItemsLabel={T("word.layers")}
              placeholder={T("word.layers")}
            />
          )}
        </div>
      </div>
      <div
        ref={ref}
        className="scada-container relative"
        onClick={cancelSelections}
        onMouseMove={e => showCords(e, isEditMode)}
      >
        <img
          alt={T("word.scada.scada-schematics")}
          className="scadaView"
          src={getScadaImageUrl(props.scadaBackgroundId)}
        />
        {!isEditMode && (
          <ScadaFreeTexts
            freeTexts={props.freeTexts}
            scadaId={scada?.scadaId}
            parent={parent}
            gridSize={gridSize}
            labelLayers={layerOptions}
          />
        )}
        <ScadaPolygons scadaId={scada?.scadaId} />
        {isEditMode && (
          <ScadaFreeTexts
            freeTexts={props.freeTexts}
            scadaId={scada?.scadaId}
            parent={parent}
            gridSize={gridSize}
            labelLayers={layerOptions}
          />
        )}
        {chunkedElements.map(elements => (
          <ScadaTags
            key={elements.map(e => e.identifier).toString()}
            {...elemProps}
            elements={elements}
          />
        ))}
      </div>
      <Tooltip id="scada-polygon-tooltip" float />
    </div>
  );
}
